import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Iconify from "../components/iconify/Iconify";
import Loading from "../components/spinners/Loading";
import { serverURL } from "../app/api/axios/http.service";
import {
  bookPublicEvent,
  getPublicEventInfo,
  getPublicEventRegistration,
  updatePublicEventRegistration,
} from "../app/api/axios/services/public.service";
import BookingStepSessions from "../components/events/booking/BookingStepSessions";
import BookingStepCustomFields from "../components/events/booking/BookingStepCustomFields";
import BookingStepSummary from "../components/events/booking/BookingStepSummary";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("de-DE");
};

const parseValidDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
  const parsed = parseValidDate(value);
  if (!parsed) return "—";
  return parsed.toLocaleString("de-DE", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const formatCountdown = (milliseconds) => {
  const totalSeconds = Math.max(
    0,
    Math.floor((Number(milliseconds) || 0) / 1000),
  );
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days} Tag${days === 1 ? "" : "e"} ${String(hours).padStart(
      2,
      "0",
    )}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0",
  )}:${String(seconds).padStart(2, "0")}`;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);
const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size <= 0) return "0 B";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};
const resolvePublicDownloadUrl = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^https?:\/\//i.test(rawValue)) return rawValue;

  const baseApiUrl = String(serverURL() || "").trim();
  if (/^https?:\/\//i.test(baseApiUrl)) {
    const apiOrigin = baseApiUrl.replace(/\/api\/?$/, "");
    return `${apiOrigin}${
      rawValue.startsWith("/") ? rawValue : `/${rawValue}`
    }`;
  }
  return `${window.location.origin}${
    rawValue.startsWith("/") ? rawValue : `/${rawValue}`
  }`;
};
const getPaymentMethodLabel = (method) => {
  if (method === "sepa") return "SEPA-Lastschrift";
  if (method === "bank_transfer") return "Überweisung";
  if (method === "not_applicable") return "Nicht erforderlich";
  return "—";
};
const DEFAULT_SEPA_DIRECT_DEBIT_CONSENT_TEXT =
  "Ich ermächtige den Veranstalter, den fälligen Betrag per SEPA-Lastschrift " +
  "von meinem Konto einzuziehen. Zugleich weise ich mein Kreditinstitut an, " +
  "die vom Veranstalter auf mein Konto gezogenen Lastschriften einzulösen. " +
  "Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, " +
  "die Erstattung des belasteten Betrags verlangen. Es gelten dabei die mit " +
  "meinem Kreditinstitut vereinbarten Bedingungen.";

const normalizeIban = (value) =>
  String(value || "")
    .replace(/\s+/g, "")
    .toUpperCase();

const formatIban = (value) =>
  normalizeIban(value)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const normalizeBic = (value) =>
  String(value || "")
    .replace(/\s+/g, "")
    .toUpperCase();

const isValidBic = (value) => {
  const bic = normalizeBic(value);
  if (!bic) return false;
  return /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic);
};

const isValidIban = (value) => {
  const iban = normalizeIban(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;

  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const expanded = rearranged
    .split("")
    .map((char) =>
      char >= "A" && char <= "Z" ? String(char.charCodeAt(0) - 55) : char,
    )
    .join("");

  let remainder = 0;
  for (let index = 0; index < expanded.length; index += 7) {
    const chunk = `${remainder}${expanded.slice(index, index + 7)}`;
    remainder = Number(chunk) % 97;
  }

  return remainder === 1;
};

const makeParticipantKey = () =>
  `p-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const createEmptyParticipant = () => ({
  _key: makeParticipantKey(),
  participantId: undefined,
  firstName: "",
  lastName: "",
  birthdate: "",
  photoOptOut: false,
  pickupPersons: [],
});

const isMissingRequiredValue = (field, value) => {
  if (!field?.required) return false;

  if (field.type === "checkbox") {
    return value !== true;
  }

  if (field.type === "multiselect") {
    return !Array.isArray(value) || value.length === 0;
  }

  if (typeof value === "string") {
    return value.trim() === "";
  }

  return value === undefined || value === null || value === "";
};

const isMinor = (birthdate) => {
  if (!birthdate) return false;
  const age =
    (new Date() - new Date(birthdate)) / (1000 * 60 * 60 * 24 * 365.25);
  return age < 18;
};

const getParticipantFieldKey = (participant, index) =>
  String(participant?._key || participant?.participantId || index);

function PublicEventBookingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { eventId, token } = useParams();
  const isEditMode = Boolean(token);

  const [event, setEvent] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [stepError, setStepError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [showBookingFlow, setShowBookingFlow] = useState(() => isEditMode);
  const [nowTimestamp, setNowTimestamp] = useState(() => Date.now());

  const [booker, setBooker] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    zip: "",
    city: "",
    country: "Deutschland",
  });
  const [paymentMethod, setPaymentMethod] = useState("");
  const [sepaMandate, setSepaMandate] = useState({
    accountHolder: "",
    iban: "",
    bic: "",
  });
  const [sepaConsentAccepted, setSepaConsentAccepted] = useState(false);
  const [paymentErrors, setPaymentErrors] = useState({});
  const [bookerErrors, setBookerErrors] = useState({});

  const [participants, setParticipants] = useState([createEmptyParticipant()]);
  const [participantErrors, setParticipantErrors] = useState({});

  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [selectedPricingRuleId, setSelectedPricingRuleId] = useState("");
  const [bookingPreviewDate, setBookingPreviewDate] = useState(() =>
    new Date().toISOString(),
  );
  const [pricingSnapshot, setPricingSnapshot] = useState(null);
  const [adminDiscount, setAdminDiscount] = useState(null);

  const [bookingCustomFieldValues, setBookingCustomFieldValues] = useState({});
  const [participantCustomFieldValues, setParticipantCustomFieldValues] =
    useState({});
  const [customFieldErrors, setCustomFieldErrors] = useState({
    booking: {},
    participants: {},
  });
  const [customFieldsStepError, setCustomFieldsStepError] = useState("");
  const [customFieldFocusTarget, setCustomFieldFocusTarget] = useState(null);

  const [consents, setConsents] = useState([]);
  const [missingConsentIds, setMissingConsentIds] = useState([]);
  const [submitError, setSubmitError] = useState("");

  const scrollToFormTop = useCallback(() => {
    const el = document.getElementById("public-event-booking-form");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const hasSessions = (event?.sessions?.length || 0) > 0;
  const hasPricingRules = (event?.pricingRules?.length || 0) > 0;
  const stepOrder = useMemo(() => {
    const order = ["booker", "participants"];
    if (hasSessions || hasPricingRules) {
      order.push("sessions");
    }
    order.push("custom");
    order.push("summary");
    return order;
  }, [hasSessions, hasPricingRules]);

  const stepLabelsById = {
    booker: "Ihre Daten",
    participants: "Teilnehmer",
    sessions: "Tage & Preis",
    custom: "Angaben",
    summary: "Zusammenfassung",
  };
  const currentStepId = stepOrder[activeStep];
  const isLastStep = activeStep === stepOrder.length - 1;
  const progressPercent = ((activeStep + 1) / stepOrder.length) * 100;
  const sepaDirectDebitConsentText =
    String(
      event?.registrationConfig?.sepaDirectDebitConsentText || "",
    ).trim() || DEFAULT_SEPA_DIRECT_DEBIT_CONSENT_TEXT;

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setLoading(true);
      setError("");
      setSuccessData(null);
      setStepError("");
      setSubmitError("");
      setMissingConsentIds([]);
      setActiveStep(0);
      setShowBookingFlow(isEditMode);
      setIsReadOnly(false);
      setPaymentMethod("");
      setSepaMandate({ accountHolder: "", iban: "", bic: "" });
      setSepaConsentAccepted(false);
      setPaymentErrors({});
      setBookingPreviewDate(new Date().toISOString());
      setPricingSnapshot(null);
      setAdminDiscount(null);

      try {
        if (isEditMode) {
          const response = await getPublicEventRegistration(eventId, token);
          const loadedEvent = response?.data?.event;
          const loadedRegistration = response?.data?.registration;
          if (!loadedEvent || !loadedRegistration) {
            throw new Error("Unvollständige Antwort");
          }
          if (!isMounted) return;

          setEvent(loadedEvent);
          setRegistrationNumber(loadedRegistration.registrationNumber || "");
          setBooker({
            firstName: loadedRegistration?.booker?.firstName || "",
            lastName: loadedRegistration?.booker?.lastName || "",
            email: loadedRegistration?.booker?.email || "",
            phone: loadedRegistration?.booker?.phone || "",
            street: loadedRegistration?.booker?.street || "",
            zip: loadedRegistration?.booker?.zip || "",
            city: loadedRegistration?.booker?.city || "",
            country: loadedRegistration?.booker?.country || "Deutschland",
          });
          setPaymentMethod(loadedRegistration?.paymentMethod || "");
          setSepaMandate({
            accountHolder: loadedRegistration?.sepaMandate?.accountHolder || "",
            iban: formatIban(loadedRegistration?.sepaMandate?.iban || ""),
            bic: loadedRegistration?.sepaMandate?.bic || "",
          });
          setSepaConsentAccepted(
            Boolean(loadedRegistration?.sepaMandate?.consentAcceptedAt),
          );

          const nextParticipants = (loadedRegistration.participants || []).map(
            (participant) => ({
              _key: participant.participantId || makeParticipantKey(),
              participantId: participant.participantId,
              firstName: participant.firstName || "",
              lastName: participant.lastName || "",
              birthdate: participant.birthdate
                ? new Date(participant.birthdate).toISOString().slice(0, 10)
                : "",
              photoOptOut: Boolean(participant.photoOptOut),
              pickupPersons: Array.isArray(participant.pickupPersons)
                ? participant.pickupPersons
                : [],
            }),
          );
          setParticipants(
            nextParticipants.length > 0
              ? nextParticipants
              : [createEmptyParticipant()],
          );

          setSelectedSessionIds(loadedRegistration.selectedSessionIds || []);
          setSelectedPricingRuleId(
            loadedRegistration.selectedPricingRuleId || "",
          );
          setBookingPreviewDate(
            loadedRegistration.createdAt || new Date().toISOString(),
          );
          setPricingSnapshot(loadedRegistration.pricingSnapshot || null);
          setAdminDiscount(loadedRegistration.adminDiscount || null);
          setBookingCustomFieldValues(
            loadedRegistration.customFieldValues || {},
          );

          const nextParticipantValues = {};
          nextParticipants.forEach((participant, index) => {
            const source = loadedRegistration.participants[index] || {};
            nextParticipantValues[getParticipantFieldKey(participant, index)] =
              {
                ...(source.customFieldValues || {}),
                _healthAllergies: source.healthData?.allergies || "",
                _healthMedications: source.healthData?.medications || "",
                _healthDietary: source.healthData?.dietaryNeeds || "",
                _emergencyName: source.healthData?.emergencyContact?.name || "",
                _emergencyPhone:
                  source.healthData?.emergencyContact?.phone || "",
                _emergencyRelation:
                  source.healthData?.emergencyContact?.relationship || "",
              };
          });
          setParticipantCustomFieldValues(nextParticipantValues);

          setConsents(
            (loadedRegistration.consents || []).map((consent) => ({
              consentId: consent.consentId,
              title: consent.title,
              acceptedAt: consent.acceptedAt,
            })),
          );
          setIsReadOnly(
            response?.data?.isReadOnly !== undefined
              ? Boolean(response.data.isReadOnly)
              : true,
          );
        } else {
          const response = await getPublicEventInfo(eventId);
          const loadedEvent = response?.data?.event;
          if (!loadedEvent) {
            throw new Error("Veranstaltung konnte nicht geladen werden");
          }
          if (!isMounted) return;
          setEvent(loadedEvent);
          setParticipants([createEmptyParticipant()]);
          setBookingPreviewDate(new Date().toISOString());
          setParticipantCustomFieldValues({});
          setConsents([]);
          setIsReadOnly(false);
          setPaymentMethod("");
          setSepaMandate({ accountHolder: "", iban: "", bic: "" });
          setSepaConsentAccepted(false);
        }
      } catch (requestError) {
        if (!isMounted) return;
        const apiMessage = requestError?.response?.data?.message;
        const status = requestError?.response?.status;
        if (status === 404) {
          setError("Diese Veranstaltung oder Anmeldung wurde nicht gefunden.");
        } else if (status === 410) {
          setError(apiMessage || "Der Zugriffslink ist abgelaufen.");
        } else if (status === 403) {
          setError(
            "Diese öffentliche Buchung ist im aktuellen Tarif nicht verfügbar.",
          );
        } else {
          setError(apiMessage || "Die Daten konnten nicht geladen werden.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (eventId) {
      load();
    }

    return () => {
      isMounted = false;
    };
  }, [eventId, token, isEditMode]);

  useEffect(() => {
    const earlyBirdRule =
      event?.registrationConfig?.discountRules?.earlyBirdDiscount || {};
    const earlyBirdType =
      earlyBirdRule?.type === "fixed" ? "fixed" : "percentage";
    const earlyBirdDeadline = parseValidDate(earlyBirdRule?.deadline);
    const hasEarlyBirdDiscount =
      earlyBirdRule?.enabled === true &&
      (earlyBirdType === "fixed"
        ? Number(earlyBirdRule?.amount) > 0
        : Number(earlyBirdRule?.percentage) > 0);

    if (
      !hasEarlyBirdDiscount ||
      !earlyBirdDeadline ||
      earlyBirdDeadline.getTime() <= Date.now()
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setNowTimestamp(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [event?.registrationConfig?.discountRules?.earlyBirdDiscount]);

  useEffect(() => {
    if (!event) return;
    if (selectedPricingRuleId) return;
    const fallbackRule =
      (event.pricingRules || []).find((rule) => rule.isDefault) ||
      (event.pricingRules || [])[0];
    if (fallbackRule?.ruleId) {
      setSelectedPricingRuleId(fallbackRule.ruleId);
    }
  }, [event, selectedPricingRuleId]);

  useEffect(() => {
    scrollToFormTop();
  }, [activeStep, scrollToFormTop]);

  const clearCustomFieldError = ({ scope, participantKey, fieldId }) => {
    setCustomFieldErrors((previous) => {
      if (scope === "booking") {
        if (!previous.booking?.[fieldId]) return previous;
        const nextBooking = { ...(previous.booking || {}) };
        delete nextBooking[fieldId];
        return { ...previous, booking: nextBooking };
      }

      const key = String(participantKey || "");
      if (!previous.participants?.[key]?.[fieldId]) return previous;
      const nextParticipantFields = { ...(previous.participants[key] || {}) };
      delete nextParticipantFields[fieldId];
      const nextParticipants = { ...(previous.participants || {}) };
      if (Object.keys(nextParticipantFields).length === 0) {
        delete nextParticipants[key];
      } else {
        nextParticipants[key] = nextParticipantFields;
      }
      return { ...previous, participants: nextParticipants };
    });
  };

  const validateCustomFieldsStep = () => {
    const customFields = event?.registrationConfig?.customFields || [];
    const bookingFields = customFields.filter(
      (field) => field.scope === "booking",
    );
    const participantFields = customFields.filter(
      (field) => field.scope === "participant",
    );

    const nextErrors = { booking: {}, participants: {} };
    let firstInvalid = null;

    bookingFields.forEach((field) => {
      const value = bookingCustomFieldValues?.[field.fieldId];
      if (!isMissingRequiredValue(field, value)) return;
      nextErrors.booking[field.fieldId] = "Dieses Feld ist erforderlich.";
      if (!firstInvalid) {
        firstInvalid = { scope: "booking", fieldId: field.fieldId };
      }
    });

    participants.forEach((participant, index) => {
      const participantKey = getParticipantFieldKey(participant, index);
      const values = participantCustomFieldValues?.[participantKey] || {};
      const minor = isMinor(participant?.birthdate);

      participantFields.forEach((field) => {
        if (field.onlyForChildren && !minor) return;
        const value = values[field.fieldId];
        if (!isMissingRequiredValue(field, value)) return;

        if (!nextErrors.participants[participantKey]) {
          nextErrors.participants[participantKey] = {};
        }
        nextErrors.participants[participantKey][field.fieldId] =
          "Dieses Feld ist erforderlich.";
        if (!firstInvalid) {
          firstInvalid = {
            scope: "participant",
            participantKey,
            fieldId: field.fieldId,
          };
        }
      });
    });

    const hasErrors =
      Object.keys(nextErrors.booking).length > 0 ||
      Object.keys(nextErrors.participants).length > 0;
    return { hasErrors, nextErrors, firstInvalid };
  };

  const validateBookerStep = () => {
    const nextErrors = {};

    if (!booker.firstName.trim())
      nextErrors.firstName = "Vorname ist erforderlich.";
    if (!booker.lastName.trim())
      nextErrors.lastName = "Nachname ist erforderlich.";
    if (!booker.email.trim()) {
      nextErrors.email = "E-Mail ist erforderlich.";
    } else if (!/^\S+@\S+\.\S+$/.test(booker.email.trim())) {
      nextErrors.email = "Bitte eine gültige E-Mail eingeben.";
    }
    if (!booker.phone.trim()) nextErrors.phone = "Telefon ist erforderlich.";
    if (!booker.street.trim()) nextErrors.street = "Straße ist erforderlich.";
    if (!booker.zip.trim()) nextErrors.zip = "PLZ ist erforderlich.";
    if (!booker.city.trim()) nextErrors.city = "Stadt ist erforderlich.";

    setBookerErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateParticipantsStep = () => {
    if (!participants.length) {
      setStepError("Bitte mindestens einen Teilnehmer angeben.");
      return false;
    }

    const nextErrors = {};
    participants.forEach((participant, index) => {
      const key = getParticipantFieldKey(participant, index);
      const participantError = {};
      if (!participant.firstName?.trim()) {
        participantError.firstName = "Vorname ist erforderlich.";
      }
      if (!participant.lastName?.trim()) {
        participantError.lastName = "Nachname ist erforderlich.";
      }
      if (!participant.birthdate) {
        participantError.birthdate = "Geburtsdatum ist erforderlich.";
      }
      if (Object.keys(participantError).length > 0) {
        nextErrors[key] = participantError;
      }
    });

    setParticipantErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStepError("Bitte prüfen Sie die Teilnehmerangaben.");
      return false;
    }
    return true;
  };

  const validateSessionsStep = () => {
    if (hasPricingRules && !selectedPricingRuleId) {
      setStepError("Bitte wählen Sie eine Preiskategorie.");
      return false;
    }
    const selectedRule = (event?.pricingRules || []).find(
      (rule) => rule.ruleId === selectedPricingRuleId,
    );
    if (
      selectedRule?.type === "per_session" &&
      selectedSessionIds.length === 0
    ) {
      setStepError("Bitte wählen Sie mindestens eine Session.");
      return false;
    }
    return true;
  };

  const validatePaymentData = () => {
    const nextErrors = {};

    if (!paymentMethod) {
      nextErrors.paymentMethod = "Bitte wählen Sie eine Zahlungsart.";
    }

    if (paymentMethod === "sepa") {
      if (!sepaMandate.accountHolder.trim()) {
        nextErrors.sepaAccountHolder = "Kontoinhaber ist erforderlich.";
      }

      const normalizedIban = normalizeIban(sepaMandate.iban);
      if (!normalizedIban) {
        nextErrors.sepaIban = "IBAN ist erforderlich.";
      } else if (!isValidIban(normalizedIban)) {
        nextErrors.sepaIban = "Bitte geben Sie eine gültige IBAN ein.";
      }

      const normalizedBic = normalizeBic(sepaMandate.bic);
      if (normalizedBic && !isValidBic(normalizedBic)) {
        nextErrors.sepaBic =
          "Bitte geben Sie eine gültige BIC ein oder lassen Sie das Feld leer.";
      }

      if (!sepaConsentAccepted) {
        nextErrors.sepaConsent =
          "Bitte stimmen Sie dem SEPA-Lastschriftverfahren zu.";
      }
    }

    setPaymentErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    setStepError("");
    if (isReadOnly) {
      setActiveStep((current) => Math.min(current + 1, stepOrder.length - 1));
      return;
    }
    if (currentStepId === "booker" && !validateBookerStep()) {
      setStepError("Bitte vervollständigen Sie Ihre Kontaktdaten.");
      scrollToFormTop();
      return;
    }
    if (currentStepId === "participants" && !validateParticipantsStep()) {
      scrollToFormTop();
      return;
    }
    if (currentStepId === "sessions" && !validateSessionsStep()) {
      scrollToFormTop();
      return;
    }
    if (currentStepId === "custom") {
      const { hasErrors, nextErrors, firstInvalid } =
        validateCustomFieldsStep();
      if (hasErrors) {
        setCustomFieldErrors(nextErrors);
        setCustomFieldsStepError(
          "Bitte füllen Sie alle als Pflichtfeld markierten Angaben aus.",
        );
        setCustomFieldFocusTarget(firstInvalid);
        scrollToFormTop();
        return;
      }
      setCustomFieldErrors({ booking: {}, participants: {} });
      setCustomFieldsStepError("");
      setCustomFieldFocusTarget(null);
    }

    setActiveStep((current) => Math.min(current + 1, stepOrder.length - 1));
  };

  const handleBack = () => {
    setStepError("");
    setActiveStep((current) => Math.max(current - 1, 0));
  };

  const handleBookerChange = (field, value) => {
    if (isReadOnly) return;
    setBooker((previous) => ({ ...previous, [field]: value }));
    setBookerErrors((previous) => ({ ...previous, [field]: "" }));
    setStepError("");
    setSubmitError("");
  };

  const handlePaymentMethodChange = (value) => {
    if (isReadOnly) return;
    setPaymentMethod(value);
    setPaymentErrors((previous) => ({
      ...previous,
      paymentMethod: "",
      sepaAccountHolder: "",
      sepaIban: "",
      sepaBic: "",
      sepaConsent: "",
    }));
    setStepError("");
    setSubmitError("");
  };

  const handleSepaMandateFieldChange = (field, value) => {
    if (isReadOnly) return;

    const normalizedValue =
      field === "iban"
        ? formatIban(value)
        : field === "bic"
        ? normalizeBic(value)
        : value;

    setSepaMandate((previous) => ({ ...previous, [field]: normalizedValue }));
    setPaymentErrors((previous) => ({
      ...previous,
      [field === "accountHolder"
        ? "sepaAccountHolder"
        : field === "iban"
        ? "sepaIban"
        : "sepaBic"]: "",
    }));
    setStepError("");
    setSubmitError("");
  };

  const handleSepaConsentChange = (checked) => {
    if (isReadOnly) return;
    setSepaConsentAccepted(checked);
    setPaymentErrors((previous) => ({ ...previous, sepaConsent: "" }));
    setStepError("");
    setSubmitError("");
  };

  const handleParticipantChange = (participantKey, field, value) => {
    if (isReadOnly) return;
    setParticipants((previous) =>
      previous.map((participant, index) =>
        getParticipantFieldKey(participant, index) === participantKey
          ? { ...participant, [field]: value }
          : participant,
      ),
    );

    setParticipantErrors((previous) => {
      if (!previous[participantKey]?.[field]) return previous;
      const next = { ...previous };
      next[participantKey] = { ...(next[participantKey] || {}), [field]: "" };
      return next;
    });

    setStepError("");
    setSubmitError("");
  };

  const addParticipant = () => {
    if (isReadOnly) return;
    const newParticipant = createEmptyParticipant();
    setParticipants((previous) => [...previous, newParticipant]);
  };

  const removeParticipant = (participantKey) => {
    if (isReadOnly) return;
    setParticipants((previous) => {
      const next = previous.filter(
        (participant, index) =>
          getParticipantFieldKey(participant, index) !== participantKey,
      );
      return next.length > 0 ? next : [createEmptyParticipant()];
    });

    setParticipantCustomFieldValues((previous) => {
      if (!previous[participantKey]) return previous;
      const next = { ...previous };
      delete next[participantKey];
      return next;
    });
  };

  const handleBookingFieldChange = (fieldId, value) => {
    if (isReadOnly) return;
    setBookingCustomFieldValues((previous) => ({
      ...previous,
      [fieldId]: value,
    }));
    clearCustomFieldError({ scope: "booking", fieldId });
    setCustomFieldsStepError("");
    setCustomFieldFocusTarget(null);
  };

  const handleParticipantFieldChange = (participantKey, fieldId, value) => {
    if (isReadOnly) return;
    setParticipantCustomFieldValues((previous) => ({
      ...previous,
      [participantKey]: {
        ...(previous[participantKey] || {}),
        [fieldId]: value,
      },
    }));
    clearCustomFieldError({ scope: "participant", participantKey, fieldId });
    setCustomFieldsStepError("");
    setCustomFieldFocusTarget(null);
  };

  const handleConsentsChange = (nextConsents) => {
    if (isReadOnly) return;
    setConsents(nextConsents);
    if (missingConsentIds.length === 0) return;
    const consentIdSet = new Set(
      (nextConsents || []).map((consent) => consent.consentId),
    );
    const stillMissing = missingConsentIds.filter(
      (id) => !consentIdSet.has(id),
    );
    setMissingConsentIds(stillMissing);
    if (stillMissing.length === 0) {
      setSubmitError("");
    }
  };

  const handleSessionsChange = (nextSessionIds) => {
    if (isReadOnly) return;
    setSelectedSessionIds(nextSessionIds);
  };

  const handlePricingRuleChange = (nextPricingRuleId) => {
    if (isReadOnly) return;
    setSelectedPricingRuleId(nextPricingRuleId);
  };

  const handleStartBookingFlow = () => {
    setShowBookingFlow(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToInfo = () => {
    setShowBookingFlow(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (isReadOnly) return;
    setSubmitError("");
    setStepError("");
    setMissingConsentIds([]);

    const requiredConsentIds = (
      event?.registrationConfig?.requiredConsents || []
    )
      .filter((consent) => consent.required !== false)
      .map((consent) => consent.consentId)
      .filter(Boolean);
    const providedConsentIds = new Set(
      (consents || []).map((consent) => consent.consentId),
    );
    const missingRequiredConsents = requiredConsentIds.filter(
      (consentId) => !providedConsentIds.has(consentId),
    );
    if (missingRequiredConsents.length > 0) {
      setMissingConsentIds(missingRequiredConsents);
      setSubmitError(
        "Bitte bestätigen Sie alle Pflicht-Einwilligungen, bevor Sie absenden.",
      );
      scrollToFormTop();
      return;
    }

    if (!validateBookerStep()) {
      setActiveStep(stepOrder.indexOf("booker"));
      setStepError("Bitte vervollständigen Sie Ihre Kontaktdaten.");
      return;
    }

    if (!validateParticipantsStep()) {
      setActiveStep(stepOrder.indexOf("participants"));
      return;
    }

    if ((hasSessions || hasPricingRules) && !validateSessionsStep()) {
      setActiveStep(stepOrder.indexOf("sessions"));
      return;
    }

    const { hasErrors, nextErrors, firstInvalid } = validateCustomFieldsStep();
    if (hasErrors) {
      setCustomFieldErrors(nextErrors);
      setCustomFieldsStepError(
        "Bitte füllen Sie alle als Pflichtfeld markierten Angaben aus.",
      );
      setCustomFieldFocusTarget(firstInvalid);
      setActiveStep(stepOrder.indexOf("custom"));
      return;
    }

    if (!validatePaymentData()) {
      setActiveStep(stepOrder.indexOf("summary"));
      setStepError("Bitte prüfen Sie die Zahlungsangaben.");
      setTimeout(() => {
        const paymentEl = document.getElementById(
          "public-booking-payment-section",
        );
        if (paymentEl) {
          paymentEl.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          scrollToFormTop();
        }
      }, 100);
      return;
    }

    try {
      setSubmitting(true);

      const participantPayload = participants.map((participant, index) => {
        const key = getParticipantFieldKey(participant, index);
        const participantValues = participantCustomFieldValues[key] || {};
        return {
          participantId: participant.participantId,
          firstName: participant.firstName.trim(),
          lastName: participant.lastName.trim(),
          birthdate: participant.birthdate || undefined,
          photoOptOut: Boolean(participant.photoOptOut),
          pickupPersons: Array.isArray(participant.pickupPersons)
            ? participant.pickupPersons
            : [],
          healthData: {
            allergies: participantValues._healthAllergies || "",
            medications: participantValues._healthMedications || "",
            dietaryNeeds: participantValues._healthDietary || "",
            emergencyContact: {
              name: participantValues._emergencyName || "",
              phone: participantValues._emergencyPhone || "",
              relationship: participantValues._emergencyRelation || "",
            },
          },
          customFieldValues: Object.fromEntries(
            Object.entries(participantValues).filter(
              ([fieldId]) =>
                !fieldId.startsWith("_health") &&
                !fieldId.startsWith("_emergency"),
            ),
          ),
        };
      });

      const payload = {
        booker: {
          ...booker,
          isExternal: true,
        },
        paymentMethod,
        sepaMandate:
          paymentMethod === "sepa"
            ? {
                accountHolder: sepaMandate.accountHolder.trim(),
                iban: normalizeIban(sepaMandate.iban),
                bic: normalizeBic(sepaMandate.bic) || undefined,
                consentAccepted: sepaConsentAccepted,
              }
            : undefined,
        participants: participantPayload,
        selectedSessionIds,
        selectedPricingRuleId: selectedPricingRuleId || undefined,
        customFieldValues: bookingCustomFieldValues,
        consents,
      };

      const response = isEditMode
        ? await updatePublicEventRegistration(eventId, token, payload)
        : await bookPublicEvent(eventId, payload);

      const data = response?.data || {};
      const resolvedRegistrationNumber =
        data.registrationNumber ||
        data.registration?.registrationNumber ||
        registrationNumber;
      const fallbackEditLink = token
        ? `/public/event/${eventId}/registration/${token}`
        : "";

      setRegistrationNumber(resolvedRegistrationNumber || "");
      setSuccessData({
        message:
          data.message ||
          (isEditMode
            ? "Anmeldung erfolgreich aktualisiert"
            : "Anmeldung erfolgreich eingegangen"),
        registrationNumber: resolvedRegistrationNumber || "",
        registrationStatus:
          data.registrationStatus ||
          data.registration?.registrationStatus ||
          "",
        editLink: data.editLink || fallbackEditLink,
        paymentInfo: data.paymentInfo || null,
        paymentMethod: data.paymentMethod || paymentMethod || "",
      });
    } catch (requestError) {
      const status = requestError?.response?.status;
      const apiMessage = requestError?.response?.data?.message;
      if (status === 409) {
        setSubmitError(
          apiMessage ||
            "Für diese E-Mail gibt es bereits eine aktive Anmeldung zu dieser Veranstaltung.",
        );
      } else if (status === 410) {
        setSubmitError(apiMessage || "Der Link ist abgelaufen.");
      } else if (status === 429) {
        setSubmitError(
          "Zu viele Anfragen in kurzer Zeit. Bitte warten Sie kurz und versuchen Sie es erneut.",
        );
      } else {
        setSubmitError(
          apiMessage || "Die Anmeldung konnte nicht gespeichert werden.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderBookerStep = () => (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Kontaktdaten der buchenden Person
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Diese Daten verwenden wir für Rückfragen, Bestätigung und
        Zahlungsinformationen.
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Vorname"
            value={booker.firstName}
            onChange={(eventValue) =>
              handleBookerChange("firstName", eventValue.target.value)
            }
            error={Boolean(bookerErrors.firstName)}
            helperText={bookerErrors.firstName}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Nachname"
            value={booker.lastName}
            onChange={(eventValue) =>
              handleBookerChange("lastName", eventValue.target.value)
            }
            error={Boolean(bookerErrors.lastName)}
            helperText={bookerErrors.lastName}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="E-Mail"
            type="email"
            value={booker.email}
            onChange={(eventValue) =>
              handleBookerChange("email", eventValue.target.value)
            }
            error={Boolean(bookerErrors.email)}
            helperText={bookerErrors.email}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Telefon"
            value={booker.phone}
            onChange={(eventValue) =>
              handleBookerChange("phone", eventValue.target.value)
            }
            error={Boolean(bookerErrors.phone)}
            helperText={bookerErrors.phone}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            label="Straße und Hausnummer"
            value={booker.street}
            onChange={(eventValue) =>
              handleBookerChange("street", eventValue.target.value)
            }
            error={Boolean(bookerErrors.street)}
            helperText={bookerErrors.street}
            required
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="PLZ"
            value={booker.zip}
            onChange={(eventValue) =>
              handleBookerChange("zip", eventValue.target.value)
            }
            error={Boolean(bookerErrors.zip)}
            helperText={bookerErrors.zip}
            required
          />
        </Grid>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            size="small"
            label="Stadt"
            value={booker.city}
            onChange={(eventValue) =>
              handleBookerChange("city", eventValue.target.value)
            }
            error={Boolean(bookerErrors.city)}
            helperText={bookerErrors.city}
            required
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderParticipantsStep = () => (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Teilnehmer hinzufügen
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Geben Sie alle Personen an, die an der Veranstaltung teilnehmen.
      </Typography>

      <Stack spacing={1.5}>
        {participants.map((participant, index) => {
          const participantKey = getParticipantFieldKey(participant, index);
          const errorsByField = participantErrors[participantKey] || {};

          return (
            <Paper key={participantKey} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.25}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle2">
                    Teilnehmer {index + 1}
                  </Typography>
                  {participants.length > 1 && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={
                        <Iconify icon="mdi:trash-can-outline" width={16} />
                      }
                      onClick={() => removeParticipant(participantKey)}
                    >
                      Entfernen
                    </Button>
                  )}
                </Stack>
                <Grid container spacing={1.25}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Vorname"
                      value={participant.firstName}
                      onChange={(eventValue) =>
                        handleParticipantChange(
                          participantKey,
                          "firstName",
                          eventValue.target.value,
                        )
                      }
                      error={Boolean(errorsByField.firstName)}
                      helperText={errorsByField.firstName}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nachname"
                      value={participant.lastName}
                      onChange={(eventValue) =>
                        handleParticipantChange(
                          participantKey,
                          "lastName",
                          eventValue.target.value,
                        )
                      }
                      error={Boolean(errorsByField.lastName)}
                      helperText={errorsByField.lastName}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Geburtsdatum"
                      type="date"
                      value={participant.birthdate}
                      onChange={(eventValue) =>
                        handleParticipantChange(
                          participantKey,
                          "birthdate",
                          eventValue.target.value,
                        )
                      }
                      error={Boolean(errorsByField.birthdate)}
                      helperText={errorsByField.birthdate}
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      control={
                        <Checkbox
                          checked={Boolean(participant.photoOptOut)}
                          onChange={(eventValue) =>
                            handleParticipantChange(
                              participantKey,
                              "photoOptOut",
                              eventValue.target.checked,
                            )
                          }
                        />
                      }
                      label="Keine Foto-/Video-Aufnahmen"
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          );
        })}

        <Button
          variant="outlined"
          startIcon={<Iconify icon="mdi:plus" width={18} />}
          onClick={addParticipant}
        >
          Weiteren Teilnehmer hinzufügen
        </Button>
      </Stack>
    </Box>
  );

  const renderPaymentSection = () => (
    <Paper
      id="public-booking-payment-section"
      variant="outlined"
      sx={{ p: 2, mb: 2 }}
    >
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Zahlungsweise</Typography>
        <FormControl
          component="fieldset"
          error={Boolean(paymentErrors.paymentMethod)}
          required
        >
          <FormLabel component="legend">
            Bitte wählen Sie die Zahlungsart
          </FormLabel>
          <RadioGroup
            row
            value={paymentMethod}
            onChange={(eventValue) =>
              handlePaymentMethodChange(eventValue.target.value)
            }
          >
            <FormControlLabel
              value="sepa"
              control={<Radio size="small" />}
              label="SEPA-Lastschrift"
              disabled={isReadOnly}
            />
            <FormControlLabel
              value="bank_transfer"
              control={<Radio size="small" />}
              label="Überweisung"
              disabled={isReadOnly}
            />
          </RadioGroup>
          <FormHelperText>{paymentErrors.paymentMethod}</FormHelperText>
        </FormControl>

        {paymentMethod === "bank_transfer" && (
          <Stack spacing={1.5}>
            <Alert severity="info">
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                Die Informationen zur Überweisung erhalten Sie in der
                Bestätigungsmail nach Abschluss der Anmeldung. Bitte überweisen
                Sie den fälligen Betrag innerhalb von 7 Tagen.
              </Typography>
            </Alert>
          </Stack>
        )}

        {paymentMethod === "sepa" && (
          <Stack spacing={1.5}>
            <Alert severity="info">
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {sepaDirectDebitConsentText}
              </Typography>
            </Alert>

            <Grid container spacing={1.25}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Kontoinhaber"
                  value={sepaMandate.accountHolder}
                  onChange={(eventValue) =>
                    handleSepaMandateFieldChange(
                      "accountHolder",
                      eventValue.target.value,
                    )
                  }
                  error={Boolean(paymentErrors.sepaAccountHolder)}
                  helperText={paymentErrors.sepaAccountHolder}
                  required
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  size="small"
                  label="IBAN"
                  value={sepaMandate.iban}
                  onChange={(eventValue) =>
                    handleSepaMandateFieldChange(
                      "iban",
                      eventValue.target.value,
                    )
                  }
                  error={Boolean(paymentErrors.sepaIban)}
                  helperText={paymentErrors.sepaIban}
                  required
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="BIC (optional)"
                  value={sepaMandate.bic}
                  onChange={(eventValue) =>
                    handleSepaMandateFieldChange("bic", eventValue.target.value)
                  }
                  error={Boolean(paymentErrors.sepaBic)}
                  helperText={paymentErrors.sepaBic}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sepaConsentAccepted}
                    onChange={(eventValue) =>
                      handleSepaConsentChange(eventValue.target.checked)
                    }
                    disabled={isReadOnly}
                  />
                }
                label="Ich stimme dem SEPA-Lastschriftverfahren zu."
              />
              {paymentErrors.sepaConsent ? (
                <FormHelperText error>
                  {paymentErrors.sepaConsent}
                </FormHelperText>
              ) : null}
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );

  const renderCurrentStep = () => {
    if (currentStepId === "booker") return renderBookerStep();
    if (currentStepId === "participants") return renderParticipantsStep();
    if (currentStepId === "sessions") {
      return (
        <BookingStepSessions
          event={event}
          selectedSessionIds={selectedSessionIds}
          selectedPricingRuleId={selectedPricingRuleId}
          participantCount={participants.length}
          memberParticipantCount={0}
          bookingDate={bookingPreviewDate}
          pricingSnapshot={pricingSnapshot}
          adminDiscount={adminDiscount}
          onSessionsChange={handleSessionsChange}
          onPricingRuleChange={handlePricingRuleChange}
        />
      );
    }
    if (currentStepId === "custom") {
      return (
        <BookingStepCustomFields
          event={event}
          participants={participants}
          bookingCustomFieldValues={bookingCustomFieldValues}
          participantCustomFieldValues={participantCustomFieldValues}
          onBookingFieldChange={handleBookingFieldChange}
          onParticipantFieldChange={handleParticipantFieldChange}
          validationErrors={customFieldErrors}
          focusField={customFieldFocusTarget}
          stepError={customFieldsStepError}
        />
      );
    }
    return (
      <Box>
        {renderPaymentSection()}
        <BookingStepSummary
          event={event}
          participants={participants}
          selectedSessionIds={selectedSessionIds}
          selectedPricingRuleId={selectedPricingRuleId}
          memberParticipantCount={0}
          bookingDate={bookingPreviewDate}
          pricingSnapshot={pricingSnapshot}
          adminDiscount={adminDiscount}
          consents={consents}
          onConsentsChange={handleConsentsChange}
          missingConsentIds={missingConsentIds}
          submitError={submitError}
          paymentMethod={paymentMethod}
        />
      </Box>
    );
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Prüfen Sie den Link oder kontaktieren Sie den Veranstalter.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const absoluteEditLink =
    successData?.editLink && !successData.editLink.startsWith("http")
      ? `${window.location.origin}${successData.editLink}`
      : successData?.editLink || "";
  const publicAttachments = Array.isArray(event?.media) ? event.media : [];
  const organizer = event?.organizer || null;
  const responsiblePerson = organizer?.responsiblePerson || null;
  const organizerAddress = [
    organizer?.address,
    [organizer?.zip, organizer?.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const organizerHomepageHref = organizer?.homepage
    ? organizer.homepage.startsWith("http")
      ? organizer.homepage
      : `https://${organizer.homepage}`
    : "";
  const eventDateLabel = formatDate(event?.eventDateBegin);
  const eventDateEndLabel = event?.eventDateEnd
    ? formatDate(event.eventDateEnd)
    : "";
  const registrationDeadline = parseValidDate(
    event?.registrationConfig?.registrationDeadline,
  );
  const discountRules = event?.registrationConfig?.discountRules || {};
  const siblingDiscountEnabled =
    discountRules?.siblingDiscount?.enabled &&
    Number(discountRules?.siblingDiscount?.percentage) > 0;
  const earlyBirdDiscountType =
    discountRules?.earlyBirdDiscount?.type === "fixed" ? "fixed" : "percentage";
  const earlyBirdDiscountAmount =
    earlyBirdDiscountType === "fixed"
      ? Number(discountRules?.earlyBirdDiscount?.amount) || 0
      : Number(discountRules?.earlyBirdDiscount?.percentage) || 0;
  const earlyBirdDeadline = parseValidDate(
    discountRules?.earlyBirdDiscount?.deadline,
  );
  const earlyBirdDiscountEnabled =
    discountRules?.earlyBirdDiscount?.enabled && earlyBirdDiscountAmount > 0;
  const earlyBirdCountdownMs = earlyBirdDeadline
    ? earlyBirdDeadline.getTime() - nowTimestamp
    : 0;
  const isEarlyBirdCurrentlyActive =
    earlyBirdDiscountEnabled && earlyBirdDeadline && earlyBirdCountdownMs > 0;
  const hasDiscountInfo = siblingDiscountEnabled || earlyBirdDiscountEnabled;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 2, sm: 4 },
        background:
          "linear-gradient(180deg, rgba(244,246,248,0.75) 0%, rgba(255,255,255,1) 30%, rgba(244,248,255,0.8) 100%)",
      }}
    >
      <Helmet>
        <title>
          MA |{" "}
          {event?.eventName
            ? `${event.eventName} – Anmeldung`
            : "Event-Anmeldung"}
        </title>
      </Helmet>

      <Container maxWidth="md">
        {/* Booking form view — shown when user starts the booking flow */}
        {(showBookingFlow || isEditMode) && !successData && (
          <Paper
            id="public-event-booking-form"
            variant="outlined"
            sx={{ p: { xs: 2, sm: 3 } }}
          >
            {/* Compact event header */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ sm: "center" }}
              sx={{ mb: 2.5 }}
            >
              <Box
                component="img"
                src={`${serverURL()}/public/tenant/image/${
                  event?.tenant || ""
                }`}
                alt="Vereinslogo"
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: "contain",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 0.5,
                  bgcolor: "background.paper",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  {event?.eventName || "Veranstaltung"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {eventDateLabel}
                  {eventDateEndLabel ? ` – ${eventDateEndLabel}` : ""}
                  {event?.locationName ? ` · ${event.locationName}` : ""}
                </Typography>
              </Box>
              {!isEditMode && (
                <Button
                  size="small"
                  startIcon={<Iconify icon="mdi:arrow-left" width={16} />}
                  onClick={handleBackToInfo}
                  sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                >
                  Zurück zur Übersicht
                </Button>
              )}
            </Stack>

            <Divider sx={{ mb: 2.5 }} />

            {isMobile ? (
              <Stack spacing={1.25} sx={{ mb: 2.5 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Chip
                    size="small"
                    color="primary"
                    label={`Schritt ${activeStep + 1}/${stepOrder.length}`}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stepLabelsById[currentStepId]}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{ height: 8, borderRadius: 999 }}
                />
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ overflowX: "auto", pb: 0.5 }}
                >
                  {stepOrder.map((stepId, index) => {
                    const isCurrent = index === activeStep;
                    const isCompleted = index < activeStep;
                    return (
                      <Chip
                        key={stepId}
                        size="small"
                        label={stepLabelsById[stepId]}
                        color={
                          isCurrent
                            ? "primary"
                            : isCompleted
                            ? "success"
                            : "default"
                        }
                        variant={isCurrent ? "filled" : "outlined"}
                        sx={{ whiteSpace: "nowrap" }}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            ) : (
              <Stepper
                activeStep={activeStep}
                alternativeLabel={false}
                orientation="horizontal"
                sx={{ mb: 3, overflowX: "auto", pb: 1 }}
              >
                {stepOrder.map((stepId) => (
                  <Step key={stepId}>
                    <StepLabel>{stepLabelsById[stepId]}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {stepError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {stepError}
              </Alert>
            )}

            {isReadOnly && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Diese Anmeldung ist nach dem Absenden schreibgeschützt. Die
                Daten können weiterhin eingesehen werden.
              </Alert>
            )}

            <Box sx={{ minHeight: 280 }}>{renderCurrentStep()}</Box>

            <Divider sx={{ my: 2 }} />
            <Stack
              direction={{ xs: "column-reverse", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
            >
              <Button
                onClick={
                  activeStep === 0 && !isEditMode
                    ? handleBackToInfo
                    : handleBack
                }
                disabled={submitting}
                startIcon={<Iconify icon="mdi:arrow-left" width={16} />}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {activeStep === 0 && !isEditMode ? "Zur Übersicht" : "Zurück"}
              </Button>
              {isLastStep ? (
                isReadOnly ? (
                  <Button
                    variant="contained"
                    disabled
                    startIcon={<Iconify icon="mdi:lock-outline" width={18} />}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    Nur Ansicht
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                    startIcon={
                      submitting ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : (
                        <Iconify icon="mdi:check" width={18} />
                      )
                    }
                  >
                    {isEditMode
                      ? "Änderungen speichern"
                      : "Kostenpflichtig anmelden"}
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={submitting}
                  endIcon={<Iconify icon="mdi:arrow-right" width={18} />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Weiter
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        {/* Event info view — hidden when booking flow is active */}
        <Stack
          spacing={2}
          sx={{
            display:
              (showBookingFlow || isEditMode) && !successData ? "none" : "flex",
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 3 },
              background:
                "linear-gradient(155deg, rgba(255,255,255,0.98) 0%, rgba(245,250,255,0.96) 100%)",
              // Hide event details when showing success view
              display: successData ? "none" : "block",
            }}
          >
            <Stack spacing={2.25}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ sm: "center" }}
              >
                <Box
                  component="img"
                  src={`${serverURL()}/public/tenant/image/${
                    event?.tenant || ""
                  }`}
                  alt="Vereinslogo"
                  sx={{
                    width: 88,
                    height: 88,
                    objectFit: "contain",
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    p: 0.75,
                    bgcolor: "background.paper",
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 0.75 }}
                  >
                    <Chip
                      size="small"
                      color="primary"
                      label="Öffentliche Anmeldung"
                    />
                    {isEditMode && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Nur Ansicht"
                      />
                    )}
                    {event?.isFull && (
                      <Chip
                        size="small"
                        color="warning"
                        label="Derzeit ausgebucht"
                      />
                    )}
                    {event?.registrationConfig?.requiresApproval && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Anmeldung mit Prüfung"
                      />
                    )}
                  </Stack>
                  <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
                    {event?.eventName || "Veranstaltung"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {eventDateLabel}
                    {eventDateEndLabel ? ` – ${eventDateEndLabel}` : ""}
                    {event?.locationName ? ` · ${event.locationName}` : ""}
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1,
                }}
              >
                <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Iconify icon="mdi:calendar-month-outline" width={18} />
                      <Typography variant="subtitle2">Termin</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {eventDateLabel}
                      {eventDateEndLabel ? ` bis ${eventDateEndLabel}` : ""}
                    </Typography>
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Iconify icon="mdi:map-marker-outline" width={18} />
                      <Typography variant="subtitle2">Ort</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {event?.locationName || "Wird bekanntgegeben"}
                    </Typography>
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Iconify
                        icon="mdi:ticket-confirmation-outline"
                        width={18}
                      />
                      <Typography variant="subtitle2">Anmeldung</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {registrationDeadline
                        ? `Frist bis ${formatDateTime(registrationDeadline)}`
                        : "Keine feste Frist hinterlegt"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Nur wenige Plätze verfügbar, schnell sein lohnt sich!
                    </Typography>
                  </Stack>
                </Paper>
              </Box>

              <Button
                variant="contained"
                onClick={handleStartBookingFlow}
                endIcon={<Iconify icon="mdi:arrow-right" width={18} />}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Jetzt zur Anmeldung
              </Button>

              {(organizer || event?.tenant) && (
                <Paper variant="outlined" sx={{ p: 1.75 }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Iconify icon="mdi:shield-account-outline" width={18} />
                      <Typography variant="subtitle2">
                        Veranstalter & Kontakt
                      </Typography>
                    </Stack>
                    <Grid container spacing={1.25}>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mb: 0.25 }}
                        >
                          {organizer?.tenantName || "Veranstalter"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {organizerAddress || "Adresse nicht hinterlegt"}
                        </Typography>
                        {organizer?.phone && (
                          <Typography
                            component="a"
                            href={`tel:${organizer.phone}`}
                            variant="body2"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              color: "inherit",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            Tel.: {organizer.phone}
                          </Typography>
                        )}
                        {organizer?.mail && (
                          <Typography
                            component="a"
                            href={`mailto:${organizer.mail}`}
                            variant="body2"
                            sx={{
                              display: "block",
                              mt: 0.25,
                              color: "inherit",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {organizer.mail}
                          </Typography>
                        )}
                        {organizer?.homepage && (
                          <Typography
                            component="a"
                            href={organizerHomepageHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              display: "block",
                              mt: 0.25,
                              color: "inherit",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {organizer.homepage}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Verantwortliche Person
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {responsiblePerson?.name || "Nicht hinterlegt"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
              )}

              {(event?.pricingRules || []).length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Preis ab{" "}
                    <strong>
                      {formatCurrency(
                        Math.min(
                          ...(event.pricingRules || []).map(
                            (rule) => Number(rule.amount) || 0,
                          ),
                        ),
                      )}
                    </strong>
                  </Typography>

                  {hasDiscountInfo && (
                    <Stack spacing={1}>
                      {earlyBirdDiscountEnabled && (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          <Stack spacing={0.5}>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                            >
                              <Chip
                                size="small"
                                color={
                                  isEarlyBirdCurrentlyActive
                                    ? "success"
                                    : "default"
                                }
                                label="Frühbucherrabatt"
                              />
                              <Typography variant="body2">
                                {earlyBirdDiscountType === "fixed"
                                  ? `${formatCurrency(
                                      earlyBirdDiscountAmount,
                                    )} Nachlass`
                                  : `${earlyBirdDiscountAmount}% Nachlass`}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {earlyBirdDeadline
                                ? `Gültig bis ${formatDateTime(
                                    earlyBirdDeadline,
                                  )}`
                                : "Keine Frist hinterlegt"}
                            </Typography>
                            {earlyBirdDeadline && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {isEarlyBirdCurrentlyActive
                                  ? `Countdown: ${formatCountdown(
                                      earlyBirdCountdownMs,
                                    )}`
                                  : "Frühbucherrabatt ist nicht mehr aktiv."}
                              </Typography>
                            )}
                          </Stack>
                        </Paper>
                      )}

                      {siblingDiscountEnabled && (
                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                          <Stack spacing={0.35}>
                            <Stack direction="row" spacing={0.75}>
                              <Chip size="small" label="Geschwisterrabatt" />
                              <Typography variant="body2">
                                {Number(
                                  discountRules?.siblingDiscount?.percentage,
                                ) || 0}
                                % Nachlass
                              </Typography>
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Gilt für zusätzliche Teilnehmer ab dem 2.
                              Teilnehmer derselben Buchung.
                            </Typography>
                          </Stack>
                        </Paper>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}

              {event?.description && (
                <>
                  <Divider />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    {event.description}
                  </Typography>
                </>
              )}

              {publicAttachments.length > 0 && (
                <>
                  <Divider />
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2">Anhänge</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Diese Dokumente und Bilder stehen für dieses öffentliche
                      Event zum Download bereit.
                    </Typography>
                    <Stack spacing={1}>
                      {publicAttachments.map((media) => {
                        const downloadUrl = resolvePublicDownloadUrl(
                          media.downloadUrl,
                        );
                        return (
                          <Paper
                            key={media._id}
                            variant="outlined"
                            sx={{ p: 1.25 }}
                          >
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              justifyContent="space-between"
                            >
                              <Stack spacing={0.25}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {media.fileName || "Datei"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {media.sizeBytes
                                    ? formatFileSize(media.sizeBytes)
                                    : "Größe unbekannt"}
                                  {media.mimeType ? ` · ${media.mimeType}` : ""}
                                </Typography>
                              </Stack>
                              <Button
                                component="a"
                                href={downloadUrl || undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                variant="outlined"
                                startIcon={
                                  <Iconify icon="mdi:download" width={16} />
                                }
                                disabled={!downloadUrl}
                              >
                                Download
                              </Button>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </Paper>

          {successData ? (
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 3, sm: 4 },
                textAlign: "center",
                background:
                  "linear-gradient(155deg, rgba(255,255,255,1) 0%, rgba(232,245,233,0.5) 100%)",
              }}
            >
              {/* Success icon */}
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                }}
              >
                <Iconify icon="mdi:check" width={40} sx={{ color: "#fff" }} />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                {successData.message}
              </Typography>

              {successData.registrationNumber && (
                <Chip
                  label={`Buchungsnummer: ${successData.registrationNumber}`}
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 2.5, fontWeight: 600 }}
                />
              )}

              {/* E-Mail-Hinweis */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  textAlign: "left",
                  bgcolor: "rgba(25, 118, 210, 0.03)",
                  borderColor: "primary.light",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Iconify
                    icon="mdi:email-check-outline"
                    width={24}
                    sx={{ color: "primary.main", mt: 0.25, flexShrink: 0 }}
                  />
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">
                      Bestätigung per E-Mail
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Wir haben Ihnen eine E-Mail mit allen Informationen zu
                      Ihrer Anmeldung
                      {successData.paymentInfo ? " und zur Zahlung" : ""}{" "}
                      gesendet.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Falls Sie keine E-Mail erhalten, prüfen Sie bitte auch
                      Ihren <strong>Spam-Ordner</strong>.
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>

              {/* Zahlungsinformation */}
              {successData.paymentInfo && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 2, textAlign: "left" }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Iconify
                      icon="mdi:credit-card-outline"
                      width={24}
                      sx={{ color: "primary.main", mt: 0.25, flexShrink: 0 }}
                    />
                    <Stack spacing={0.75} sx={{ width: "100%" }}>
                      <Typography variant="subtitle2">
                        Zahlungsinformation
                      </Typography>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">
                          <strong>Zahlungsart:</strong>{" "}
                          {getPaymentMethodLabel(
                            successData.paymentInfo.method ||
                              successData.paymentMethod,
                          )}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Betrag:</strong>{" "}
                          {formatCurrency(successData.paymentInfo.amount)}
                        </Typography>
                        {successData.paymentInfo.reference && (
                          <Typography variant="body2">
                            <strong>Verwendungszweck:</strong>{" "}
                            {successData.paymentInfo.reference}
                          </Typography>
                        )}
                        {successData.paymentInfo.recipient && (
                          <Typography variant="body2">
                            <strong>Empfänger:</strong>{" "}
                            {successData.paymentInfo.recipient}
                          </Typography>
                        )}
                        {successData.paymentInfo.iban && (
                          <Typography variant="body2">
                            <strong>IBAN:</strong>{" "}
                            {successData.paymentInfo.iban}
                          </Typography>
                        )}
                        {successData.paymentInfo.bic && (
                          <Typography variant="body2">
                            <strong>BIC:</strong> {successData.paymentInfo.bic}
                          </Typography>
                        )}
                      </Stack>
                      {successData.paymentInfo.customText && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {successData.paymentInfo.customText}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              )}

              {/* Zugriffslink */}
              {absoluteEditLink && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 2, textAlign: "left" }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Iconify
                      icon="mdi:link-variant"
                      width={24}
                      sx={{ color: "primary.main", mt: 0.25, flexShrink: 0 }}
                    />
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2">
                        Ihr Zugriffslink
                      </Typography>
                      <Typography
                        component="a"
                        href={absoluteEditLink}
                        variant="body2"
                        sx={{
                          wordBreak: "break-all",
                          color: "primary.main",
                        }}
                      >
                        {absoluteEditLink}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              )}

              {/* Kontakt-Hinweis */}
              <Divider sx={{ my: 2 }} />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="center"
                sx={{ color: "text.secondary" }}
              >
                <Iconify icon="mdi:help-circle-outline" width={18} />
                <Typography variant="body2">
                  Bei Fragen oder Problemen wenden Sie sich bitte an den{" "}
                  {organizer?.mail ? (
                    <Typography
                      component="a"
                      href={`mailto:${organizer.mail}`}
                      variant="body2"
                      sx={{ color: "primary.main", fontWeight: 500 }}
                    >
                      Veranstalter
                    </Typography>
                  ) : (
                    "Veranstalter"
                  )}
                  .
                </Typography>
              </Stack>
            </Paper>
          ) : !showBookingFlow && !isEditMode ? (
            <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack
                spacing={1.25}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
              >
                <Typography variant="subtitle1">
                  Alle Infos geprüft? Dann starten Sie jetzt Ihre Anmeldung.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Der Ablauf führt Sie Schritt für Schritt durch Kontaktdaten,
                  Teilnehmer, Preis und Zahlung.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleStartBookingFlow}
                  endIcon={<Iconify icon="mdi:arrow-right" width={18} />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Jetzt zur Anmeldung
                </Button>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}

export default PublicEventBookingPage;
